import * as React from 'react';
import { useEffect, useState } from 'react';
import type { OnlineApplicationChoice } from '../../../OnlineApplication/Model';
import type { LegacyOnlineStudyPlanViewModel } from '../ViewModel/StudyPlanner/Legacy/LegacyOnlineStudyPlanViewModel.csharp';
import { TimeType } from '~/Pages/OnlineStudyPage/Semester/ParentStudyViewModel';
import type { TabKey } from '~/Partials/TabList/Tab';
import type { OnlineSemesterViewModel } from './BySemester/Semester/Semester';
import type { OnlineSemesterItemViewModel } from './BySemester/SubjectsBySemester';
import { SubjectsBySemester } from './BySemester/SubjectsBySemester';
import type { OnlineStudyPlanContextProps } from './Context/OnlineStudyPlanContext';
import { OnlineStudyPlanContext } from './Context/OnlineStudyPlanContext';
import type { OnlineStudyTextsViewModel } from './OnlineStudyTextsViewModel.csharp';
import { SingleSubjects } from './SingleSubjects/SingleSubjects';
import { SubjectsTabs } from './Tabs/SubjectsTabs';

export const OnlineStudyPlan: React.FC<React.PropsWithChildren<LegacyOnlineStudyPlanViewModel>> = props => {
    const {
        fullTimeSemesters: fullTimeSemestersProps,
        partTimeSemesters: partTimeSemestersProps,
        allAvailableObligatorySubjects,
        onlineApplicationFormUrl,
        pointsInBasketExceededWarningThreshold,
        allAvailableElectiveSubjects,
        displayPartTimeSemesters,
        translations: {
            fullTime: fullTimeTabName,
            partTime: partTimeTabName,
            individualCourses: individualCoursesTabName,
            selectTab,
        },
        fullTimeSemesterHoursPerWeek,
        partTimeSemesterHoursPerWeek,
        fullTimeStudyCode,
        partTimeStudyCode,
    } = props;

    const initializeSemesterItems = (item: OnlineSemesterItemViewModel, itemIndex: number) => ({
        ...item,
        index: itemIndex,
        selectedElectiveSubjects: [],
        hasOpenedAvailableElectiveSubjects: false,
        availableElectiveSubjects:
            item.availableElectiveSubjects.length > 0 ? item.availableElectiveSubjects : allAvailableElectiveSubjects,
    });

    const initializeSemester = (semester: OnlineSemesterViewModel, semesterIndex: number) => ({
        ...semester,
        index: semesterIndex,
        items: semester.items.map(initializeSemesterItems),
    });

    const fullTimeSemesters = (fullTimeSemestersProps as OnlineSemesterViewModel[]).map(initializeSemester);
    const partTimeSemesters = (partTimeSemestersProps as OnlineSemesterViewModel[]).map(initializeSemester);

    const showFullTimeSemesters = fullTimeSemesters.length > 0;
    const showPartTimeSemesters = displayPartTimeSemesters && partTimeSemesters.length > 0;

    useEffect(() => {
        if (!showFullTimeSemesters) {
            const newCurrentTab = showPartTimeSemesters ? partTimeTabName : individualCoursesTabName;
            setCurrentTab(newCurrentTab);
        }
    }, []);

    const setChoicesFromBasket = (setter: (prevChoices: OnlineApplicationChoice[]) => OnlineApplicationChoice[]) => {
        setState(prevState => {
            const newChoices = setter(prevState.choicesFromBasket);

            return { ...prevState, choicesFromBasket: newChoices };
        });
    };

    const setCurrentTab = (tabKey: TabKey) => {
        setState(prevState => ({ ...prevState, currentTab: tabKey }));
    };

    const getText = (textId: keyof OnlineStudyTextsViewModel) => {
        const { translations } = props;

        return (translations || {})[textId] || `[${textId}]`;
    };

    const setAddChoicesToBasketHandler = (
        addChoicesToBasketHandler: (choices: OnlineApplicationChoice | OnlineApplicationChoice[]) => void
    ) => {
        setState(prevState => ({ ...prevState, addChoicesToBasketHandler }));
    };

    const setRemoveChoicesFromBasketHandler = (
        removeChoicesFromBasketHandler: (choices: OnlineApplicationChoice | OnlineApplicationChoice[]) => void
    ) => {
        setState(prevState => ({ ...prevState, removeChoicesFromBasketHandler }));
    };

    const getHoursPerWeekFromTimeType = (timeType: TimeType) =>
        timeType === TimeType.fullTime ? fullTimeSemesterHoursPerWeek : partTimeSemesterHoursPerWeek;

    const getStudyCodeWithFallback = (timeType: TimeType) => {
        switch (timeType) {
            case TimeType.fullTime:
                return fullTimeStudyCode || partTimeStudyCode;
            case TimeType.partTime:
                return partTimeStudyCode || fullTimeStudyCode;
            default:
                throw new Error(`Unrecognized study timeType value: ${timeType}.`);
        }
    };

    const initialState = {
        setChoicesFromBasket,
        setCurrentTab,
        setAddChoicesToBasketHandler,
        setRemoveChoicesFromBasketHandler,
        getText,
        getHoursPerWeekFromTimeType,
        currentTab: fullTimeTabName,
        onlineApplicationFormUrl,
        pointsInBasketExceededWarningThreshold,
        choicesFromBasket: [],
        addChoicesToBasketHandler: () => {},
        removeChoicesFromBasketHandler: () => {},
    };

    const [state, setState]: [
        OnlineStudyPlanContextProps,
        React.Dispatch<React.SetStateAction<OnlineStudyPlanContextProps>>,
    ] = useState<OnlineStudyPlanContextProps>(initialState);

    return (
        <div>
            <OnlineStudyPlanContext.Provider value={state}>
                <div>
                    <SubjectsTabs tabsLabel={selectTab}>
                        {showFullTimeSemesters && (
                            <SubjectsBySemester
                                key={TimeType.fullTime}
                                tabName={fullTimeTabName}
                                parentStudy={{
                                    timeType: TimeType.fullTime,
                                    code: getStudyCodeWithFallback(TimeType.fullTime),
                                }}
                                semesters={fullTimeSemesters}
                            />
                        )}
                        {showPartTimeSemesters && (
                            <SubjectsBySemester
                                key={TimeType.partTime}
                                tabName={partTimeTabName}
                                parentStudy={{
                                    timeType: TimeType.partTime,
                                    code: getStudyCodeWithFallback(TimeType.partTime),
                                }}
                                semesters={partTimeSemesters}
                            />
                        )}
                        <SingleSubjects
                            key="Single"
                            tabName={individualCoursesTabName}
                            allAvailableObligatorySubjects={allAvailableObligatorySubjects}
                            allAvailableElectiveSubjects={allAvailableElectiveSubjects}
                            parentStudy={{
                                timeType: TimeType.partTime,
                                code: getStudyCodeWithFallback(TimeType.partTime),
                            }}
                        />
                    </SubjectsTabs>
                </div>
            </OnlineStudyPlanContext.Provider>
        </div>
    );
};
